/**
 * 色调映射预览：单一 currentToneMappingItem 的 before/after 解析（P1）。
 */

export type ToneMappingCurrentItem = {
  id: string;
  mainSnapshot: string;
  shadowSnapshot?: string;
  resultSnapshot: string;
  thumbUrl?: string;
  scenePanelWidthPx?: number;
  scenePanelHeightPx?: number;
  timestamp: number;
  ownerEmail?: string;
};

export function toneMappingCompareBefore(current: ToneMappingCurrentItem | null): string | null {
  const fromCurrent = current?.mainSnapshot?.trim();
  return fromCurrent || null;
}

export function toneMappingCompareAfter(current: ToneMappingCurrentItem | null): string | null {
  const r = current?.resultSnapshot?.trim();
  return r || null;
}

export function toneMappingCompareSliderKey(
  current: ToneMappingCurrentItem | null,
  before: string | null,
  after: string | null,
): string {
  if (!current?.id) return 'tone-compare-empty';
  return `${current.id}|${before || ''}|${after || ''}`;
}

/** 与 toneHistory 同步时需比较的预览字段（不含 id） */
export function toneMappingPreviewFieldsEqual(
  a: ToneMappingCurrentItem | null,
  b: ToneMappingCurrentItem | null,
): boolean {
  if (!a || !b) return a === b;
  if (a.id !== b.id) return false;
  const s = (v?: string) => String(v ?? '').trim();
  return (
    s(a.mainSnapshot) === s(b.mainSnapshot) &&
    s(a.shadowSnapshot) === s(b.shadowSnapshot) &&
    s(a.resultSnapshot) === s(b.resultSnapshot) &&
    s(a.thumbUrl) === s(b.thumbUrl) &&
    (a.scenePanelWidthPx ?? 0) === (b.scenePanelWidthPx ?? 0) &&
    (a.scenePanelHeightPx ?? 0) === (b.scenePanelHeightPx ?? 0) &&
    a.timestamp === b.timestamp
  );
}

/** 裁切缓存：仅允许 item.id 精确命中 */
export function getToneMappingCroppedResultForId(
  itemId: string,
  croppedResultById: ReadonlyMap<string, string>,
): string | undefined {
  const id = String(itemId ?? '').trim();
  if (!id) return undefined;
  const cached = croppedResultById.get(id);
  if (!cached?.trim()) return undefined;
  return cached.trim();
}

/**
 * 从单条历史构建原子 current：main/result 均来自同一 item；
 * result 仅可用同 id 的裁切缓存，禁止跨项 fallback。
 */
export function buildToneMappingCurrentFromItem(
  item: ToneMappingCurrentItem,
  croppedResultById?: ReadonlyMap<string, string>,
): ToneMappingCurrentItem {
  const s = (v?: string) => String(v ?? '').trim();
  const id = s(item.id);
  const mainSnapshot = s(item.mainSnapshot);
  const shadowSnapshot = s(item.shadowSnapshot);
  const freshResult = s(item.resultSnapshot);
  let resultSnapshot = '';
  if (id && croppedResultById) {
    const cached = getToneMappingCroppedResultForId(id, croppedResultById);
    if (cached) {
      resultSnapshot = cached;
    } else if (freshResult) {
      resultSnapshot = freshResult;
    }
  } else if (freshResult) {
    resultSnapshot = freshResult;
  }
  return {
    ...item,
    id,
    mainSnapshot,
    shadowSnapshot: shadowSnapshot || undefined,
    resultSnapshot,
  };
}

/** toneHistory 刷新后剔除不在列表中的裁切缓存，防止串 id */
export function pruneToneMappingCroppedResultCache(
  cache: Map<string, string>,
  validIds: readonly string[],
): void {
  const keep = new Set(validIds.map((x) => String(x).trim()).filter(Boolean));
  for (const key of [...cache.keys()]) {
    if (!keep.has(key)) cache.delete(key);
  }
}

/**
 * toneHistory 刷新后重绑定：快照仅来自 fresh；裁切缓存仅 fresh.id 命中。
 * 禁止把 current 的 main/result 合并进 fresh（即使 id 相同）。
 */
export function mergeToneMappingCurrentFromHistory(
  fresh: ToneMappingCurrentItem,
  current: ToneMappingCurrentItem,
  croppedResultById: ReadonlyMap<string, string>,
): ToneMappingCurrentItem {
  if (current.id !== fresh.id) {
    return buildToneMappingCurrentFromItem(fresh, croppedResultById);
  }
  return buildToneMappingCurrentFromItem(fresh, croppedResultById);
}

/** 删除当前项后：优先下一条（同序），否则上一条，否则 null */
export function pickToneMappingCurrentAfterDelete(
  items: ToneMappingCurrentItem[],
  deletedId: string,
): ToneMappingCurrentItem | null {
  const idx = items.findIndex((x) => x.id === deletedId);
  if (idx < 0) return items[0] ?? null;
  const remaining = items.filter((x) => x.id !== deletedId);
  if (!remaining.length) return null;
  return remaining[Math.min(idx, remaining.length - 1)] ?? null;
}

/** P3 诊断：统一 trace 字段（临时，勿用于业务逻辑） */
export type ToneMappingP3TraceInput = {
  action: string;
  clickedId?: string | null;
  currentId?: string | null;
  toneHistoryIds?: string[];
  current?: ToneMappingCurrentItem | null;
  compareBase?: string | null;
  compareAfter?: string | null;
  croppedResultById?: ReadonlyMap<string, string>;
  /** 查 cropped 缓存用的 id，默认 currentId ?? clickedId */
  cacheLookupId?: string | null;
  /** P3.5：点击项快照 */
  clickedItemMainSnapshot80?: string;
  clickedItemResultSnapshot80?: string;
  /** P3.5：裁切序列 */
  cropSeq?: number;
  targetItemId?: string | null;
  croppedResult80?: string;
  seqDiscarded?: boolean;
  /** P3.5：光栅化输出 / state */
  outputBefore80?: string;
  outputAfter80?: string;
  stateBefore80?: string;
  stateAfter80?: string;
  extra?: Record<string, unknown>;
};

export function toneMappingSnip80(raw: string | null | undefined): string {
  const t = String(raw ?? '').trim();
  if (!t) return '';
  return t.length <= 80 ? t : `${t.slice(0, 80)}…`;
}

export function toneMappingP3Trace(input: ToneMappingP3TraceInput): void {
  const current = input.current ?? null;
  const currentId = input.currentId ?? current?.id ?? null;
  const clickedId = input.clickedId ?? null;
  const lookupId = input.cacheLookupId ?? currentId ?? clickedId;
  const cache = input.croppedResultById;
  const cacheVal = lookupId && cache ? cache.get(lookupId) : undefined;
  console.log('[TONE_P3_TRACE]', {
    action: input.action,
    clickedId,
    currentId,
    toneHistoryIds: input.toneHistoryIds ?? [],
    currentMainSnapshot80: toneMappingSnip80(current?.mainSnapshot),
    currentResultSnapshot80: toneMappingSnip80(current?.resultSnapshot),
    compareBase80: toneMappingSnip80(input.compareBase),
    compareAfter80: toneMappingSnip80(input.compareAfter),
    clickedItemMainSnapshot80: input.clickedItemMainSnapshot80 ?? '',
    clickedItemResultSnapshot80: input.clickedItemResultSnapshot80 ?? '',
    cropSeq: input.cropSeq ?? null,
    targetItemId: input.targetItemId ?? null,
    croppedResult80: input.croppedResult80 ?? '',
    seqDiscarded: input.seqDiscarded ?? false,
    outputBefore80: input.outputBefore80 ?? '',
    outputAfter80: input.outputAfter80 ?? '',
    stateBefore80: input.stateBefore80 ?? '',
    stateAfter80: input.stateAfter80 ?? '',
    croppedCacheHasId: !!(lookupId && cache?.has(lookupId)),
    croppedCacheValue80: toneMappingSnip80(cacheVal),
    timestamp: Date.now(),
    ...(input.extra ?? {}),
  });
}
